/* eslint-disable no-param-reassign */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/label-has-associated-control */
// eslint-disable-next-line import/no-extraneous-dependencies
import React, { useCallback, useState, useEffect, useRef } from 'react'
// import * as d3 from 'd3'
import {
    allPossibleStats,
    mapPercentileNames,
    flipGoodTypesBatter,
    flipGoodTypesPitcher,
} from 'bam-node-baseball-savant/apps/utils/possible-comp-tool-stats.ts'
import { DragDropProvider } from '@dnd-kit/react'
import { isSortable } from '@dnd-kit/react/sortable'
import { RestrictToHorizontalAxis } from '@dnd-kit/abstract/modifiers'
import { arrayMove } from '@dnd-kit/helpers'
import {
    PointerSensor,
    PointerActivationConstraints,
    AutoScroller,
} from '@dnd-kit/dom'
import { toPng } from 'html-to-image'
import SortableWrapper from './SortableWrapper.tsx'

import PlayerComponent from './PlayerComponent.tsx'
import PlayerSearch from './PlayerSearch.tsx'
import StatPicker from './StatPicker.tsx'
import StatColumn from './StatColumn.tsx'
import SectionHeaders from './SectionHeaders.tsx'
import RemoveButton from './RemoveButton.tsx'
import SavantLogo from './SavantLogo.tsx'
import SettingsMenu from './SettingsMenu.tsx'
import {
    SVG_HEIGHT,
    RIGHT_SIDE_BUFFER,
    LEFT_SIDE_BUFFER,
    SIDE_BUFFER,
    IMG_TOP_BUFFER,
    SAVANT_GREEN,
    STAT_HEADER_GAP,
    LINE_HEIGHT,
    ADD_STAT_BUTTON_DIM,
    DARKER_SAVANT_GREEN,
    TITLE_HEIGHT,
    PLAYERS_PER_MOBILE_PAGE,
    BTWN_PAN_BUFFER,
    NO_PLAYERS_WIDTH,
    TEAM_LOGO_SIZE,
} from './utils/constants.ts'
import {
    groupStatSelects,
    DEFAULT_BATTING_STATS,
    DEFAULT_PITCHING_STATS,
} from './utils/stat-groupings.ts'
import {
    updateUrl,
    getUniqueId,
    getType,
    isStatsFull,
} from './utils/helpers.ts'
import {
    PlayerRow,
    CompToolPlayer,
    StatGroup,
    SummaryStatRow,
} from './utils/types.ts'
import {
    DimensionsProvider,
    getDimensions,
    MAX_SVG_WIDTH,
    useContainerWidth,
} from './utils/responsive-dimensions.tsx'
import { ModeProvider, useMode } from './utils/mode.tsx'
import ExportDateStamp from './ExportDateStamp.tsx'
import SettingsToggle from './SettingsToggle.tsx'
import ShareButton from './ShareButton.tsx'

declare const playerSeasons: CompToolPlayer[]
declare const initialData: PlayerRow[]
declare const initialStats: (keyof PlayerRow)[]
declare const summaryStats: SummaryStatRow[]
declare const trendingBatters: CompToolPlayer[]
declare const trendingPitchers: CompToolPlayer[]

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        s: any
    }
}

function useComparisonState() {
    const initialDataToUse = [...initialData]

    const [selectedData, setSelectedData] = useState({
        players: initialDataToUse.map((dat, idx) => ({
            id: dat.uniqueId,
            displayOrder: idx,
        })),
        dataToUse: initialDataToUse,
    })
    const [selectedStats, setSelectedStats] =
        useState<(keyof PlayerRow)[]>(initialStats)
    const [showWinner, setShowWinner] = useState(false)
    const [showValues, setShowValues] = useState(false)
    const [showTitle, setShowTitle] = useState(false)
    const [showTeams, setShowTeams] = useState(true)
    const [title, setTitle] = useState('')
    const [triggerSearchOpen, setTriggerSearchOpen] = useState(false)

    // Parse URL params once on load to seed the title and optionally open search
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)

        const presetTitle = urlParams.get('showTitle') === 'true'
        const presetTitleText = urlParams.get('title')

        if (presetTitle && presetTitleText && presetTitleText.length > 0) {
            setShowTitle(true)
            setTitle(presetTitleText)
        }

        const shouldOpenSearch = urlParams.get('openSearch') === 'true'
        const shouldShowWinners = urlParams.get('showWinner') === 'true'
        const shouldShowValues = urlParams.get('showValues') === 'true'
        const shouldHideTeams = urlParams.get('hideTeams') === 'true'

        if (shouldOpenSearch) {
            setTriggerSearchOpen(true)
        }

        if (shouldShowWinners) {
            setShowWinner(true)
        }

        if (shouldShowValues) {
            setShowValues(true)
        }

        if (shouldHideTeams) {
            setShowTeams(false)
        }
    }, [])

    // Keep the URL in sync with the current selection
    useEffect(() => {
        const sortedIds = [...selectedData.players]
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((p) => p.id)

        updateUrl(
            sortedIds,
            selectedStats,
            showWinner,
            showValues,
            showTeams,
            showTitle,
            title
        )
    }, [
        selectedData,
        selectedStats,
        showWinner,
        showValues,
        showTitle,
        showTeams,
        title,
    ])

    // Open the player search whenever there are no players selected
    useEffect(() => {
        if (selectedData.players.length === 0) {
            setTriggerSearchOpen(true)
        }
    }, [selectedData.players.length])

    return {
        selectedData,
        setSelectedData,
        selectedStats,
        setSelectedStats,
        showWinner,
        setShowWinner,
        showValues,
        setShowValues,
        showTitle,
        setShowTitle,
        title,
        setTitle,
        showTeams,
        setShowTeams,
        triggerSearchOpen,
        setTriggerSearchOpen,
    }
}

type ComparisonState = ReturnType<typeof useComparisonState>

type ComparisonToolViewProps = {
    layoutWidth: number
    shared: ComparisonState
    exportSvgRef: React.RefObject<SVGSVGElement>
}

function ComparisonToolView({
    layoutWidth,
    shared,
    exportSvgRef,
}: ComparisonToolViewProps) {
    const mode = useMode()
    const windowWidth = layoutWidth
    const isExport = mode === 'export'
    const dimensions = getDimensions(windowWidth)
    const {
        SVG_WIDTH,
        MAX_FULL_PLAYERS,
        MAX_TOTAL_PLAYERS,
        ADD_BUTTON_DIM,
        TEXT_RIGHT_SIDE,
        SLIDER_OFFSET_FROM_TEXT,
        BETWEEN_PLAYERS_BUFFER,
        LOGO_WIDTH,
        TOP_BUFFER,
        TITLE_TOP_BUFFER,
        isMobile,
    } = dimensions

    const {
        selectedData,
        setSelectedData,
        selectedStats,
        setSelectedStats,
        showWinner,
        setShowWinner,
        showValues,
        setShowValues,
        showTitle,
        setShowTitle,
        title,
        setTitle,
        showTeams,
        setShowTeams,
        triggerSearchOpen,
        setTriggerSearchOpen,
    } = shared

    const [hoveredPlayer, setHoveredPlayer] = useState<string | null>(null)
    const [hoveredAddPlayer, setHoveredAddPlayer] = useState(false)
    const [hoveredPanRight, setHoveredPanRight] = useState(false)
    const [hoveredPanLeft, setHoveredPanLeft] = useState(false)
    const [hoveredAddStat, setHoveredAddStat] = useState(false)
    const [mobilePageIndex, setMobilePageIndex] = useState(0)
    const [isDownloading, setIsDownloading] = useState(false)
    const searchRef = useRef<HTMLDivElement>(null)
    const statRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (triggerSearchOpen && searchRef.current) {
            searchRef.current.showPopover()
            setTriggerSearchOpen(false)
        }
    }, [triggerSearchOpen, shared])

    const maxMobileIndex = Math.max(
        0,
        selectedData.players.length - PLAYERS_PER_MOBILE_PAGE
    )

    const possibleStats = allPossibleStats.filter((field) =>
        field.includes('percent_rank_')
    )

    const dataMappedByPlayer = selectedData.dataToUse.reduce((acc, curr) => {
        acc[`${curr.id}-${curr.year}-${curr.grouping_cat?.toLowerCase()}`] =
            curr
        return acc
    }, {} as Record<string, PlayerRow>)

    const isEmpty = selectedData.players.length === 0
    const isFull = selectedData.players.length === MAX_TOTAL_PLAYERS
    const isNarrow = selectedData.players.length > MAX_FULL_PLAYERS

    const currentType = getType(selectedData)
    const lastActiveType = useRef(currentType)

    useEffect(() => {
        if (!isEmpty && currentType) {
            lastActiveType.current = currentType
        }
    }, [currentType, isEmpty])

    const groupedSelects: Record<StatGroup, (keyof PlayerRow)[]> =
        groupStatSelects(selectedStats, currentType)
    const existingTypes = (Object.keys(groupedSelects) as StatGroup[]).filter(
        (type) => groupedSelects[type].length > 0
    ) as StatGroup[]

    const winners: Record<string, string> = {}

    if (!isEmpty) {
        selectedStats.forEach((stat) => {
            const valueKey = mapPercentileNames(stat)

            let winner = selectedData.players[0].id
            selectedData.players.forEach((playerObj) => {
                const player = playerObj.id
                const flipType =
                    (flipGoodTypesBatter.includes(stat) &&
                        currentType === 'batter') ||
                    (flipGoodTypesPitcher.includes(stat) &&
                        currentType === 'pitcher')

                if (
                    Number(dataMappedByPlayer[player][valueKey]) >=
                        Number(dataMappedByPlayer[winner][valueKey]) &&
                    !flipType &&
                    dataMappedByPlayer[player][stat] !== null
                ) {
                    winner = player
                }

                if (
                    Number(dataMappedByPlayer[player][valueKey]) <
                        Number(dataMappedByPlayer[winner][valueKey]) &&
                    flipType &&
                    dataMappedByPlayer[player][stat] !== null
                ) {
                    winner = player
                }

                if (
                    dataMappedByPlayer[player][valueKey] !== null &&
                    dataMappedByPlayer[winner][valueKey] === null
                ) {
                    winner = player
                }
            })

            if (dataMappedByPlayer[winner][stat] !== null) {
                winners[stat] = winner
            }
        })
    }

    const handleAddPlayer = useCallback(
        async (row: { playerData: CompToolPlayer; displayOrder: number }[]) => {
            if (row.length === 0) {
                setSelectedData({ dataToUse: [], players: [] })
                return
            }

            const newType = row[0].playerData.grouping_cat.toLowerCase()

            if (newType !== lastActiveType.current) {
                setSelectedStats(
                    newType === 'pitcher'
                        ? DEFAULT_PITCHING_STATS
                        : DEFAULT_BATTING_STATS
                )

                lastActiveType.current = newType as 'batter' | 'pitcher'
            }

            let url = `/comparison-tool/services/`
            let objString = ``

            row.forEach((player) => {
                objString += `${getUniqueId(player.playerData)},`
            })
            objString = objString.slice(0, -1)
            url += objString

            const { playerData } = await fetch(url).then((res) => res.json())

            const newPlayers = row
                .map((newRow) => ({
                    id: newRow.playerData.uniqueId,
                    displayOrder: newRow.displayOrder,
                }))
                .sort((a, b) => a.displayOrder - b.displayOrder)

            setSelectedData({
                players: newPlayers,
                dataToUse: playerData,
            })
        },
        [selectedData]
    )

    const handleAddStat = useCallback(
        (row: string[]) => {
            setSelectedStats(row as (keyof PlayerRow)[])
        },
        [selectedStats]
    )

    const handleRemoveStat = useCallback(
        (thisStat: string) => {
            setSelectedStats((prev) => prev.filter((stat) => stat !== thisStat))
        },
        [selectedStats]
    )

    const explicitInlineStyles = (
        originalElem: Element,
        cloneElem: Element
    ) => {
        const svgProperties = [
            'fill',
            'stroke',
            'stroke-width',
            'font-family',
            'font-size',
            'font-weight',
            'opacity',
            'display',
            'text-anchor',
            'dominant-baseline',
            'text-transform',
        ]

        const applyStylesRecursively = (orig: Element, clone: Element) => {
            if (!(clone instanceof HTMLElement || clone instanceof SVGElement))
                return

            const computedStyles = window.getComputedStyle(orig)

            svgProperties.forEach((prop) => {
                const value = computedStyles.getPropertyValue(prop)
                if (value) {
                    clone.style.setProperty(prop, value)
                }
            })

            const origChildren = Array.from(orig.children)
            const cloneChildren = Array.from(clone.children)

            origChildren.forEach((origChild, index) => {
                const cloneChild = cloneChildren[index]
                if (cloneChild) {
                    applyStylesRecursively(origChild, cloneChild)
                }
            })
        }

        applyStylesRecursively(originalElem, cloneElem)
    }

    const handleDownload = useCallback(() => {
        // Always export from the hidden desktop-width view so mobile users get
        // the full desktop layout regardless of the current on-screen size.
        const exportSvg = exportSvgRef.current

        if (exportSvg === null) {
            return
        }

        setIsDownloading(true)

        // Rudimentary click analytics
        if (window.s) {
            const downloadType = `Comp tool ${
                isMobile ? 'mobile' : 'desktop'
            } download`
            window.s.prop31 = downloadType
            window.s.tl(true, 'd', downloadType)
        }

        // Size the export from the viewBox coordinate numbers, NOT clientWidth:
        // with `max-width: 100%` the painted width no longer equals the viewBox
        // width, so clientWidth/clientHeight are unreliable.
        const exportWidth = getDimensions(MAX_SVG_WIDTH).SVG_WIDTH
        const exportHeight = SVG_HEIGHT

        const svgClone = exportSvg.cloneNode(true) as SVGElement

        explicitInlineStyles(exportSvg, svgClone)

        const styleNode = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'style'
        )
        styleNode.textContent = `
            .comp-tool-canvas text { font-family: 'Roboto Condensed', sans-serif !important; }
        `
        svgClone.prepend(styleNode)

        // Paint at the viewBox aspect ratio (SVG_WIDTH:SVG_HEIGHT); pixelRatio
        // below controls the actual output resolution.
        svgClone.setAttribute('width', `${exportWidth}`)
        svgClone.setAttribute('height', `${exportHeight}`)

        const hiddenDiv = document.createElement('div')
        hiddenDiv.style.position = 'absolute'
        hiddenDiv.style.top = '0px'
        hiddenDiv.style.left = '0px'
        hiddenDiv.style.zIndex = '-1000'

        hiddenDiv.style.opacity = '0'
        hiddenDiv.style.pointerEvents = 'none'

        hiddenDiv.style.width = `${exportWidth}px`
        hiddenDiv.style.height = `${exportHeight}px`
        hiddenDiv.style.backgroundColor = '#ffffff'

        document.body.appendChild(hiddenDiv)
        hiddenDiv.appendChild(svgClone)

        toPng(hiddenDiv, {
            quality: 0.95,
            pixelRatio: 3,
            backgroundColor: '#ffffff',
            style: {
                opacity: '1',
            },
        })
            .then(async (dataUrl) => {
                if (isMobile && navigator.canShare) {
                    try {
                        const response = await fetch(dataUrl)
                        const blob = await response.blob()

                        const file = new File([blob], 'savant-comparison.png', {
                            type: 'image/png',
                        })

                        if (navigator.canShare({ files: [file] })) {
                            await navigator.share({
                                title: 'Baseball Savant Comparison',
                                text: 'Check out this player comparison from Baseball Savant!',
                                files: [file],
                            })
                            return
                        }
                    } catch (error) {
                        console.error('Sharing failed or was aborted:', error)
                    }
                }

                const link = document.createElement('a')
                link.download = 'savant-comparison-tool.png'
                link.href = dataUrl
                link.click()
            })
            .catch((error) => {
                console.error('png generation failed', error)
            })
            .finally(() => {
                setIsDownloading(false)
                if (document.body.contains(hiddenDiv)) {
                    document.body.removeChild(hiddenDiv)
                }
            })
    }, [exportSvgRef, isMobile])

    function handleRemovePlayer(player: string) {
        const players = selectedData.players.filter((p) => p.id !== player)
        const dataToUse = selectedData.dataToUse.filter(
            (d) => d.uniqueId !== player
        )

        setSelectedData({ players, dataToUse })
    }

    let rightBufferToUse = isFull ? SIDE_BUFFER : RIGHT_SIDE_BUFFER

    if (isMobile && selectedData.players.length <= PLAYERS_PER_MOBILE_PAGE) {
        rightBufferToUse += 8
    } else if (isMobile) {
        rightBufferToUse -= 12
    }

    useEffect(() => {
        if (mobilePageIndex > maxMobileIndex) {
            setMobilePageIndex(maxMobileIndex)
        }
    }, [maxMobileIndex, mobilePageIndex])

    // Sort players by displayOrder for rendering
    const sortedPlayers = [...selectedData.players].sort(
        (a, b) => a.displayOrder - b.displayOrder
    )

    const displayedPlayers =
        !isExport &&
        isMobile &&
        selectedData.players.length > PLAYERS_PER_MOBILE_PAGE
            ? sortedPlayers.slice(
                  mobilePageIndex,
                  mobilePageIndex + PLAYERS_PER_MOBILE_PAGE
              )
            : sortedPlayers

    const showPanRightButton =
        !isExport &&
        isMobile &&
        selectedData.players.length > PLAYERS_PER_MOBILE_PAGE &&
        mobilePageIndex < maxMobileIndex

    const showPanLeftButton =
        !isExport &&
        isMobile &&
        selectedData.players.length > PLAYERS_PER_MOBILE_PAGE &&
        mobilePageIndex > 0

    const showBothPans = showPanRightButton && showPanLeftButton

    const showAddButton =
        !isExport &&
        !isFull &&
        !isEmpty &&
        !(isMobile && selectedData.players.length > PLAYERS_PER_MOBILE_PAGE)

    const showAddStatButton =
        !isExport &&
        !isEmpty &&
        !isStatsFull(
            selectedStats,
            currentType,
            showTitle,
            showTeams,
            false,
            dimensions
        )

    const safeDenom = isEmpty ? 1 : displayedPlayers.length

    const maxSliderWidth =
        (SVG_WIDTH -
            LEFT_SIDE_BUFFER -
            SLIDER_OFFSET_FROM_TEXT -
            SIDE_BUFFER -
            rightBufferToUse) /
            safeDenom -
        BETWEEN_PLAYERS_BUFFER

    const rightMostXVal =
        SIDE_BUFFER +
        SLIDER_OFFSET_FROM_TEXT +
        displayedPlayers.length * (BETWEEN_PLAYERS_BUFFER + maxSliderWidth)

    const bottomMostYVal =
        existingTypes.length * STAT_HEADER_GAP +
        selectedStats.length * LINE_HEIGHT +
        TOP_BUFFER

    const logoXVal =
        (LEFT_SIDE_BUFFER + SIDE_BUFFER + SLIDER_OFFSET_FROM_TEXT) / 2 +
        SIDE_BUFFER

    type DragOverEvent = Parameters<
        NonNullable<React.ComponentProps<typeof DragDropProvider>['onDragOver']>
    >[0]

    const handleDragOver = (event: DragOverEvent) => {
        const { operation } = event
        const { source, target } = operation

        if (isSortable(source) && isSortable(target)) {
            const overIndex = target.sortable.index
            const activeIndex = source.sortable.index

            if (activeIndex !== overIndex) {
                setSelectedData((prev) => {
                    // Get visual order and perform the move
                    const currentVisualOrder = [...prev.players].sort(
                        (a, b) => a.displayOrder - b.displayOrder
                    )
                    const reordered = arrayMove(
                        currentVisualOrder,
                        activeIndex,
                        overIndex
                    )

                    // Re-map displayOrder so the indices match the new slots
                    const newPlayers = reordered.map((p, i) => ({
                        ...p,
                        displayOrder: i,
                    }))

                    return { ...prev, players: newPlayers }
                })
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleStatReorder = (event: any) => {
        const { operation } = event
        const { source, target } = operation

        if (isSortable(source) && isSortable(target)) {
            const activeId = source.id
            const overId = target.id
            const sourceGroup = source.sortable.group
            const targetGroup = target.sortable.group

            if (sourceGroup !== targetGroup) {
                return
            }

            if (activeId !== overId) {
                setSelectedStats((prev) => {
                    const oldIndex = prev.indexOf(activeId as keyof PlayerRow)
                    const newIndex = prev.indexOf(overId as keyof PlayerRow)

                    return arrayMove(prev, oldIndex, newIndex)
                })
            }
        }
    }

    const teamOffset = showTeams ? 0 : -TEAM_LOGO_SIZE

    const titleOffset = showTitle && title.trim() !== '' ? TITLE_HEIGHT : 0
    const titleWillOverflow = isStatsFull(
        selectedStats,
        currentType,
        true,
        showTeams,
        true,
        dimensions
    )

    return (
        <div className="App">
            <DimensionsProvider width={windowWidth}>
                <DragDropProvider
                    plugins={(defaults) =>
                        defaults.filter((plugin) => plugin !== AutoScroller)
                    }
                    onDragStart={() => {
                        document.body.style.overflow = 'hidden'
                    }}
                    onDragEnd={() => {
                        document.body.style.overflow = ''
                    }}
                    onDragOver={handleDragOver}
                    modifiers={[RestrictToHorizontalAxis]}
                    sensors={(defaults) => [
                        ...defaults.filter(
                            (sensor) => sensor !== PointerSensor
                        ),
                        PointerSensor.configure({
                            activationConstraints: [
                                new PointerActivationConstraints.Distance({
                                    value: 12,
                                }),
                                new PointerActivationConstraints.Delay({
                                    value: 300,
                                    tolerance: 10,
                                }),
                            ],
                        }),
                    ]}
                >
                    <svg
                        className="comp-tool-canvas"
                        width={SVG_WIDTH}
                        height={SVG_HEIGHT}
                        ref={isExport ? exportSvgRef : undefined}
                        viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
                    >
                        {showTitle && title.trim() !== '' && (
                            <g
                                id={`${mode}-comp-tool-title`}
                                transform={`translate(${
                                    SVG_WIDTH / 2
                                }, ${TITLE_TOP_BUFFER})`}
                            >
                                <text
                                    textAnchor="middle" // This perfectly centers the text on the X coordinate
                                    dominantBaseline="hanging"
                                    fontSize="20px"
                                    fontWeight="bold"
                                    fill="#333333"
                                >
                                    {title}
                                </text>
                            </g>
                        )}

                        <g
                            transform={`translate(0, ${titleOffset})`}
                            style={{ transition: 'transform 0.2s ease-in-out' }}
                        >
                            <g
                                id={`${mode}-comp-tool-logo`}
                                transform={`translate(${logoXVal}, ${IMG_TOP_BUFFER})`}
                            >
                                <SavantLogo
                                    width={
                                        showTeams ? LOGO_WIDTH : LOGO_WIDTH - 4
                                    }
                                />
                            </g>
                            {!isExport && <SettingsToggle />}

                            {!isExport && (
                                <ShareButton
                                    isEmpty={isEmpty}
                                    onDownload={handleDownload}
                                />
                            )}

                            {!isEmpty && (
                                <g id={`${mode}-comp-tool-section-headers`}>
                                    <SectionHeaders
                                        numPlayers={displayedPlayers.length}
                                        groupedSelects={groupedSelects}
                                        existingTypes={existingTypes}
                                        maxSliderWidth={maxSliderWidth}
                                        showTeams={showTeams}
                                    />
                                </g>
                            )}
                            {isEmpty && (
                                <g
                                    transform={`translate(${SVG_WIDTH / 2}, ${
                                        SVG_HEIGHT / 2
                                    })`}
                                    onClick={() => {
                                        searchRef.current?.showPopover()
                                    }}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'opacity 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.opacity = '0.7'
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.opacity = '1'
                                    }}
                                >
                                    <rect
                                        x={-NO_PLAYERS_WIDTH / 2}
                                        y={-TOP_BUFFER / 2}
                                        width={NO_PLAYERS_WIDTH}
                                        height={TOP_BUFFER}
                                        rx={8}
                                        fill={SAVANT_GREEN}
                                        fillOpacity={0.05}
                                        stroke={SAVANT_GREEN}
                                        strokeWidth={1.5}
                                    />
                                    <text
                                        x={0}
                                        y={-12}
                                        fill={DARKER_SAVANT_GREEN}
                                        className="no-players-text"
                                    >
                                        No players selected!
                                    </text>
                                    <text
                                        x={0}
                                        y={12}
                                        fill={DARKER_SAVANT_GREEN}
                                        className="no-players-text"
                                    >
                                        Click to select players
                                    </text>
                                </g>
                            )}
                            <g transform={`translate(${LEFT_SIDE_BUFFER}, 0)`}>
                                {!isEmpty && (
                                    <StatColumn
                                        groupedSelects={groupedSelects}
                                        existingTypes={existingTypes}
                                        currentType={
                                            currentType as 'batter' | 'pitcher'
                                        }
                                        onRemoveStat={handleRemoveStat}
                                        onStatReorder={handleStatReorder}
                                        selectedStats={selectedStats}
                                        showTeams={showTeams}
                                    />
                                )}
                                {displayedPlayers.map((playerObj, i) => {
                                    const player = playerObj.id
                                    const thisData = dataMappedByPlayer[player]

                                    const xVal =
                                        SIDE_BUFFER +
                                        SLIDER_OFFSET_FROM_TEXT +
                                        i *
                                            (BETWEEN_PLAYERS_BUFFER +
                                                maxSliderWidth)
                                    const xButtonOffset = isMobile ? 18 : 28
                                    return (
                                        <SortableWrapper
                                            key={player}
                                            id={player}
                                            index={i}
                                            offset={xVal}
                                            type="player"
                                            maxSliderWidth={maxSliderWidth}
                                            bottomMostYVal={bottomMostYVal}
                                        >
                                            <g
                                                onMouseEnter={() =>
                                                    setHoveredPlayer(player)
                                                }
                                                onMouseLeave={() =>
                                                    setHoveredPlayer(null)
                                                }
                                                pointerEvents="auto"
                                            >
                                                <rect
                                                    x={0}
                                                    y={0}
                                                    width={maxSliderWidth}
                                                    height={bottomMostYVal}
                                                    fill="transparent"
                                                    pointerEvents="all" // Forces the empty space to catch hover events
                                                />

                                                <PlayerComponent
                                                    key={player}
                                                    selects={selectedStats}
                                                    playerData={thisData}
                                                    groupedSelects={
                                                        groupedSelects
                                                    }
                                                    existingTypes={
                                                        existingTypes
                                                    }
                                                    maxSliderWidth={
                                                        maxSliderWidth
                                                    }
                                                    showValues={showValues}
                                                    showWinner={showWinner}
                                                    winners={winners}
                                                    isNarrow={isNarrow}
                                                    summaryStats={summaryStats}
                                                    currentType={currentType}
                                                    numPlayers={
                                                        displayedPlayers.length
                                                    }
                                                    showTeams={showTeams}
                                                />
                                                <rect
                                                    x={0}
                                                    y={IMG_TOP_BUFFER}
                                                    width={maxSliderWidth}
                                                    height={TOP_BUFFER}
                                                    stroke="none"
                                                    fill="transparent"
                                                    strokeWidth="1px"
                                                />
                                                <g
                                                    onPointerDown={(e) => {
                                                        e.stopPropagation()
                                                    }}
                                                    onMouseDown={(e) => {
                                                        e.stopPropagation()
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleRemovePlayer(
                                                            player
                                                        )
                                                    }}
                                                    style={{
                                                        opacity:
                                                            hoveredPlayer ===
                                                            player
                                                                ? 1
                                                                : 0,
                                                        pointerEvents:
                                                            hoveredPlayer ===
                                                            player
                                                                ? 'auto'
                                                                : 'none',
                                                    }}
                                                    className="remove-button-x-elem"
                                                >
                                                    <RemoveButton
                                                        x={
                                                            maxSliderWidth / 2 +
                                                            xButtonOffset
                                                        }
                                                        y={IMG_TOP_BUFFER + 7}
                                                        symbol="×"
                                                    />
                                                </g>
                                            </g>
                                        </SortableWrapper>
                                    )
                                })}
                                {showAddButton && (
                                    <g
                                        transform={`translate(${rightMostXVal}, 0)`}
                                        onMouseEnter={() =>
                                            setHoveredAddPlayer(true)
                                        }
                                        onMouseLeave={() =>
                                            setHoveredAddPlayer(false)
                                        }
                                        onClick={() => {
                                            searchRef.current?.showPopover()
                                        }}
                                        id={`${mode}-comp-tool-add-player-wrapper`}
                                        style={{
                                            cursor: 'pointer',
                                            opacity: hoveredAddPlayer ? 1 : 0.2,
                                            transition:
                                                'opacity 0.2s ease-in-out',
                                        }}
                                    >
                                        <rect
                                            x={0}
                                            y={IMG_TOP_BUFFER}
                                            width={ADD_BUTTON_DIM}
                                            height={TOP_BUFFER}
                                            rx={8}
                                            stroke={SAVANT_GREEN}
                                            fill={SAVANT_GREEN}
                                        />
                                        <RemoveButton
                                            x={ADD_BUTTON_DIM / 2}
                                            y={IMG_TOP_BUFFER + TOP_BUFFER / 2}
                                            symbol="+"
                                        />
                                    </g>
                                )}

                                {showPanRightButton && (
                                    <g
                                        transform={`translate(${rightMostXVal}, 0)`}
                                        onMouseEnter={() =>
                                            setHoveredPanRight(true)
                                        }
                                        onMouseLeave={() =>
                                            setHoveredPanRight(false)
                                        }
                                        onClick={() => {
                                            setHoveredPanRight(false)
                                            setMobilePageIndex(
                                                (prev) => prev + 1
                                            )
                                        }}
                                        id={`${mode}-comp-tool-pan-player-right-wrapper`}
                                        className="remove-button-x-elem"
                                        style={{
                                            opacity: hoveredPanRight ? 1 : 0.2,
                                        }}
                                    >
                                        <rect
                                            x={0}
                                            y={
                                                showBothPans
                                                    ? IMG_TOP_BUFFER +
                                                      TOP_BUFFER / 2 +
                                                      BTWN_PAN_BUFFER / 2
                                                    : IMG_TOP_BUFFER
                                            }
                                            width={ADD_BUTTON_DIM}
                                            height={
                                                showBothPans
                                                    ? (TOP_BUFFER -
                                                          BTWN_PAN_BUFFER) /
                                                      2
                                                    : TOP_BUFFER
                                            }
                                            rx={8}
                                            stroke={SAVANT_GREEN}
                                            fill={SAVANT_GREEN}
                                        />
                                        <RemoveButton
                                            x={ADD_BUTTON_DIM / 2}
                                            y={
                                                showBothPans
                                                    ? IMG_TOP_BUFFER +
                                                      (3 * TOP_BUFFER) / 4 +
                                                      BTWN_PAN_BUFFER / 4
                                                    : IMG_TOP_BUFFER +
                                                      TOP_BUFFER / 2
                                            }
                                            symbol=">"
                                        />
                                    </g>
                                )}

                                {showPanLeftButton && (
                                    <g
                                        transform={`translate(${rightMostXVal}, 0)`}
                                        onMouseEnter={() =>
                                            setHoveredPanLeft(true)
                                        }
                                        onMouseLeave={() =>
                                            setHoveredPanLeft(false)
                                        }
                                        onClick={() => {
                                            setHoveredPanLeft(false)
                                            setMobilePageIndex(
                                                (prev) => prev - 1
                                            )
                                        }}
                                        id={`${mode}-comp-tool-pan-player-left-wrapper`}
                                        className="remove-button-x-elem"
                                        style={{
                                            opacity: hoveredPanLeft ? 1 : 0.2,
                                        }}
                                    >
                                        <rect
                                            x={0}
                                            y={IMG_TOP_BUFFER}
                                            width={ADD_BUTTON_DIM}
                                            height={
                                                showBothPans
                                                    ? (TOP_BUFFER -
                                                          BTWN_PAN_BUFFER) /
                                                      2
                                                    : TOP_BUFFER
                                            }
                                            rx={8}
                                            stroke={SAVANT_GREEN}
                                            fill={SAVANT_GREEN}
                                        />
                                        <RemoveButton
                                            x={ADD_BUTTON_DIM / 2}
                                            y={
                                                showBothPans
                                                    ? IMG_TOP_BUFFER +
                                                      (TOP_BUFFER -
                                                          BTWN_PAN_BUFFER) /
                                                          4
                                                    : IMG_TOP_BUFFER +
                                                      TOP_BUFFER / 2
                                            }
                                            symbol="<"
                                        />
                                    </g>
                                )}
                            </g>

                            {showAddStatButton && (
                                <g
                                    transform={`translate(${SIDE_BUFFER}, ${
                                        bottomMostYVal + teamOffset
                                    })`}
                                    onMouseEnter={() => setHoveredAddStat(true)}
                                    onMouseLeave={() =>
                                        setHoveredAddStat(false)
                                    }
                                    onClick={() => {
                                        statRef.current?.showPopover()
                                    }}
                                    style={{
                                        cursor: 'pointer',
                                        opacity: hoveredAddStat ? 1 : 0.2,
                                        transition: 'opacity 0.2s ease-in-out',
                                    }}
                                    id={`${mode}-comp-tool-add-stat-wrapper`}
                                >
                                    <rect
                                        x={0}
                                        y={0}
                                        width={
                                            TEXT_RIGHT_SIDE + LEFT_SIDE_BUFFER
                                        }
                                        height={ADD_STAT_BUTTON_DIM}
                                        rx={8}
                                        stroke={SAVANT_GREEN}
                                        fill={SAVANT_GREEN}
                                    />
                                    <RemoveButton
                                        x={
                                            (TEXT_RIGHT_SIDE +
                                                LEFT_SIDE_BUFFER) /
                                            2
                                        }
                                        y={ADD_STAT_BUTTON_DIM / 2}
                                        symbol="+"
                                    />
                                </g>
                            )}

                            {isMobile &&
                                (showPanLeftButton || showPanRightButton) && (
                                    <g
                                        transform={`translate(${
                                            SVG_WIDTH - 20
                                        }, ${IMG_TOP_BUFFER - 7})`}
                                        id={`${mode}-mobile-overflow-selection-label`}
                                    >
                                        <text
                                            style={{
                                                textAnchor: 'end',
                                                fontSize: '11px',
                                            }}
                                        >
                                            Showing{' '}
                                            {`${mobilePageIndex + 1}-${
                                                mobilePageIndex +
                                                PLAYERS_PER_MOBILE_PAGE
                                            }`}{' '}
                                            of {sortedPlayers.length}
                                        </text>
                                    </g>
                                )}
                        </g>
                        {isExport && (
                            <ExportDateStamp
                                width={SVG_WIDTH}
                                height={SVG_HEIGHT}
                            />
                        )}
                    </svg>
                </DragDropProvider>

                {!isExport && (
                    <>
                        <PlayerSearch
                            searchRef={searchRef}
                            data={playerSeasons}
                            preloadedData={selectedData}
                            trendingBatters={trendingBatters.slice(0, 10)}
                            trendingPitchers={trendingPitchers.slice(0, 10)}
                            onAdd={handleAddPlayer}
                        />
                        <StatPicker
                            statRef={statRef}
                            stats={possibleStats}
                            selected={selectedStats}
                            currentType={currentType}
                            showTitle={showTitle}
                            showTeams={showTeams}
                            onAdd={handleAddStat}
                        />
                        <SettingsMenu
                            showWinner={showWinner}
                            setShowWinner={setShowWinner}
                            showValues={showValues}
                            setShowValues={setShowValues}
                            showTitle={showTitle}
                            setShowTitle={setShowTitle}
                            title={title}
                            setTitle={setTitle}
                            showTeams={showTeams}
                            setShowTeams={setShowTeams}
                            handleDownload={handleDownload}
                            disableToggleTitle={!showTitle && titleWillOverflow}
                        />
                    </>
                )}
            </DimensionsProvider>

            {isDownloading && (
                <div className="comp-tool-download-dialog-container">
                    <div className="comp-tool-download-spinner" />
                    <div className="comp-tool-download-text">
                        Generating Image...
                    </div>
                </div>
            )}
        </div>
    )
}

function App() {
    const containerRef = useRef<HTMLDivElement>(null)
    const layoutWidth = useContainerWidth(containerRef)
    const shared = useComparisonState()
    const exportSvgRef = useRef<SVGSVGElement>(null)

    return (
        <>
            <div ref={containerRef} className="comp-tool-container">
                <ModeProvider mode="interactive">
                    <ComparisonToolView
                        layoutWidth={layoutWidth}
                        shared={shared}
                        exportSvgRef={exportSvgRef}
                    />
                </ModeProvider>
            </div>
            <div
                aria-hidden="true"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    opacity: 0,
                    pointerEvents: 'none',
                    zIndex: -1000,
                    overflow: 'hidden',
                }}
            >
                <ModeProvider mode="export">
                    <ComparisonToolView
                        layoutWidth={MAX_SVG_WIDTH}
                        shared={shared}
                        exportSvgRef={exportSvgRef}
                    />
                </ModeProvider>
            </div>
        </>
    )
}

export default App
